import { useMemo, useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { allCountries } from '@/utils/countries';

/**
 * Compact country selector for the phone-auth input.
 *
 * Renders as a pill button showing flag + dial code (e.g. "🇺🇸 +1").
 * Click opens a bottom-sheet / popover with search + full list.
 *
 * Props:
 *   value      ISO-2 country code (e.g. 'US')
 *   onChange   (iso2) => void
 */
export default function CountryPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const { popular, rest, byIso } = useMemo(() => allCountries(), []);
  const selected = byIso[value] || byIso.US;

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 60);
    if (!open) setQuery('');
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? [...popular, ...rest].filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dialCode.replace('+', '').includes(q.replace('+', '')) ||
          c.iso2.toLowerCase().includes(q)
      )
    : null;

  const pick = (iso2) => {
    onChange(iso2);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="country-picker-btn"
        aria-label="Select country code"
        className="inline-flex items-center gap-1.5 px-3 py-3.5 rounded-l-xl bg-[#F8F9FA] border border-r-0 border-black/[0.06] hover:bg-[#F1F3F5] transition-colors text-[13px] font-medium text-[#1A1A1A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EF4444]/50"
      >
        <span className="text-[15px] leading-none">{selected.flag}</span>
        <span className="tabular-nums text-[13px] leading-none">{selected.dialCode}</span>
        <ChevronDown size={12} strokeWidth={2} className="text-zinc-400 -ml-0.5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
          data-testid="country-picker-sheet"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-sm sm:mx-4 bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[80vh] flex flex-col animate-in slide-in-from-bottom-8 sm:slide-in-from-bottom-4 duration-200"
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <p className="font-brand text-lg font-[500] text-[#0F0F10]">Country code</p>
              <button
                onClick={() => setOpen(false)}
                data-testid="country-picker-close"
                className="w-8 h-8 rounded-full hover:bg-black/[0.04] flex items-center justify-center"
              >
                <X size={16} strokeWidth={2} className="text-zinc-500" />
              </button>
            </div>

            <div className="px-5 pb-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" strokeWidth={2} />
                <input
                  ref={searchRef}
                  type="text"
                  inputMode="search"
                  data-testid="country-picker-search"
                  placeholder="Search country or code"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#F8F9FA] border border-black/[0.06] text-[13px] text-[#0F0F10] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#EF4444]/40"
                />
              </div>
            </div>

            <div className="overflow-y-auto flex-1 pb-4">
              {filtered ? (
                filtered.length === 0 ? (
                  <p className="text-center text-sm text-zinc-500 py-10">No matches</p>
                ) : (
                  filtered.map((c) => (
                    <CountryRow key={c.iso2} c={c} onPick={pick} isSelected={c.iso2 === value} />
                  ))
                )
              ) : (
                <>
                  <SectionLabel>Popular</SectionLabel>
                  {popular.map((c) => (
                    <CountryRow key={c.iso2} c={c} onPick={pick} isSelected={c.iso2 === value} />
                  ))}
                  <SectionLabel>All countries</SectionLabel>
                  {rest.map((c) => (
                    <CountryRow key={c.iso2} c={c} onPick={pick} isSelected={c.iso2 === value} />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const SectionLabel = ({ children }) => (
  <p className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">{children}</p>
);

const CountryRow = ({ c, onPick, isSelected }) => (
  <button
    onClick={() => onPick(c.iso2)}
    data-testid={`country-row-${c.iso2}`}
    className={`w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-black/[0.03] transition-colors ${
      isSelected ? 'bg-[#EF4444]/[0.06]' : ''
    }`}
  >
    <span className="text-[18px] leading-none flex-shrink-0">{c.flag}</span>
    <span className="flex-1 text-[13.5px] font-medium text-[#0F0F10] truncate">{c.name}</span>
    <span className="text-[12.5px] tabular-nums text-zinc-500 flex-shrink-0">{c.dialCode}</span>
  </button>
);
